import React, { useState, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Panel from "aldoo-ra/Panel"
import ColorPicker from "../components/ColorPicker"
import ImagePropertyEditor from "./ImagePropertyEditor"

const GenericPropertyGroupEditor = ({ properties, onChange, group }) => {
  const [activeColorPicker, setActiveColorPicker] = useState(null)
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 })

  if (!group?.properties) return null

  // Handle property changes
  const handlePropertyChange = (property, value) => {
    onChange?.(property, value)
  }

  // Check if any property has a value
  const hasAnyValue = Object.entries(group.properties).some(([property]) =>
    properties[property] && properties[property].toString().trim()
  )

  // Get icon component based on icon name
  const getIconElement = () => {
    if (!group.icon) return null

    // For now, we'll use a simple approach - you could extend this with a proper icon resolver
    const iconClasses = "w-4 h-4 text-admin_text_secondary"

    switch (group.icon) {
      case 'photo':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 7.5v11.25Z" />
        </svg>
      case 'arrows-expand':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      case 'square-2-stack':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 8.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v8.25A2.25 2.25 0 0 0 6 16.5h2.25m8.25-8.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25H9.75A2.25 2.25 0 0 1 7.5 18v-2.25" />
        </svg>
      case 'document-text':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      case 'view-columns':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0v10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2z" />
        </svg>
      case 'arrows-pointing-out':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      case 'sparkles':
        return <svg className={iconClasses} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      default:
        return <div className={iconClasses} />
    }
  }

  const titleElement = (
    <div className="flex items-center space-x-2">
      {getIconElement()}
      <Typography variant="admin_body_small" className={`font-medium ${hasAnyValue ? 'text-admin_primary' : 'text-admin_text'}`}>
        {group.label}
      </Typography>
    </div>
  )

  // Render individual property editor based on type
  const renderPropertyEditor = (propertyKey, propertyConfig) => {
    const value = properties[propertyKey] || ""

    switch (propertyConfig.type) {
      case 'text':
        return (
          <div key={propertyKey} className="space-y-2">
            <Typography variant="admin_body_small" className="font-medium capitalize text-admin_text_secondary text-sm">
              {propertyConfig.label || propertyKey}
            </Typography>
            <Input
              variant="admin"
              value={value}
              onChange={(e) => handlePropertyChange(propertyKey, e.target.value)}
              placeholder={propertyConfig.placeholder || ""}
              debounce={300}
              readOnly={propertyConfig.readOnly || false}
              className={propertyConfig.readOnly ? "bg-admin_neutral_1" : ""}
            />
          </div>
        )

      case 'select':
        return (
          <div key={propertyKey} className="space-y-2">
            <Typography variant="admin_body_small" className="font-medium capitalize text-admin_text_secondary text-sm">
              {propertyConfig.label || propertyKey}
            </Typography>
            <DropDownSelect
              variant="admin"
              searchEnabled={true}
              options={propertyConfig.options || []}
              value={propertyConfig.options?.find(opt => opt.value === (value || '')) || { value: '', name: 'Default' }}
              onChange={(selected) => {
                const selectedValue = Array.isArray(selected) ? selected[0]?.value : selected?.value
                handlePropertyChange(propertyKey, selectedValue)
              }}
              placeholder={propertyConfig.placeholder || `Select ${propertyConfig.label || propertyKey}`}
            />
          </div>
        )

      case 'color':
        return (
          <div key={propertyKey} className="space-y-2">
            <Typography variant="admin_body_small" className="font-medium capitalize text-admin_text_secondary text-sm">
              {propertyConfig.label || propertyKey}
            </Typography>
            <div className="flex gap-2">
              <Input
                variant="admin"
                value={value}
                onChange={(e) => handlePropertyChange(propertyKey, e.target.value)}
                placeholder={propertyConfig.placeholder || ""}
                debounce={300}
                className="flex-1"
              />
              <Button
                variant="admin_secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  const buttonRect = e.currentTarget.getBoundingClientRect()
                  const preferredPosition = propertyConfig.colorPickerPosition || 'topLeft'

                  // ColorPicker dimensions (w-96 = 384px width, estimated height ~280px)
                  const pickerWidth = 384
                  const pickerHeight = 280
                  const offset = 8

                  // Viewport dimensions
                  const viewportWidth = window.innerWidth
                  const viewportHeight = window.innerHeight

                  // Calculate all possible positions
                  const positions = {
                    topLeft: {
                      top: buttonRect.top - offset,
                      left: buttonRect.left,
                      transform: 'translateY(-100%)'
                    },
                    topRight: {
                      top: buttonRect.top - offset,
                      left: buttonRect.right - pickerWidth,
                      transform: 'translateY(-100%)'
                    },
                    bottomLeft: {
                      top: buttonRect.bottom + offset,
                      left: buttonRect.left
                    },
                    bottomRight: {
                      top: buttonRect.bottom + offset,
                      left: buttonRect.right - pickerWidth
                    }
                  }

                  // Check which positions would be within viewport
                  const getPositionScore = (pos, name) => {
                    const actualTop = name.startsWith('top') ? pos.top - pickerHeight : pos.top
                    const actualLeft = pos.left

                    let score = 0

                    // Check if fully within viewport
                    if (actualTop >= 0 && actualTop + pickerHeight <= viewportHeight) score += 10
                    if (actualLeft >= 0 && actualLeft + pickerWidth <= viewportWidth) score += 10

                    // Prefer positions closer to preferred position
                    if (name === preferredPosition) score += 5

                    // Penalty for going off-screen
                    if (actualTop < 0) score -= Math.abs(actualTop)
                    if (actualTop + pickerHeight > viewportHeight) score -= (actualTop + pickerHeight - viewportHeight)
                    if (actualLeft < 0) score -= Math.abs(actualLeft)
                    if (actualLeft + pickerWidth > viewportWidth) score -= (actualLeft + pickerWidth - viewportWidth)

                    return score
                  }

                  // Find best position
                  let bestPosition = preferredPosition
                  let bestScore = getPositionScore(positions[preferredPosition], preferredPosition)

                  Object.entries(positions).forEach(([name, pos]) => {
                    const score = getPositionScore(pos, name)
                    if (score > bestScore) {
                      bestScore = score
                      bestPosition = name
                    }
                  })

                  setColorPickerPosition(positions[bestPosition])
                  setActiveColorPicker({ property: propertyKey, value, config: propertyConfig })
                }}
                className="px-3"
                style={{ backgroundColor: value || 'transparent' }}
              >
                🎨
              </Button>
            </div>
          </div>
        )

      case 'image':
        return (
          <ImagePropertyEditor
            key={propertyKey}
            property={propertyKey}
            value={value}
            onChange={handlePropertyChange}
            propertyConfig={propertyConfig}
          />
        )

      default:
        // Fallback to text input
        return (
          <div key={propertyKey} className="space-y-2">
            <Typography variant="admin_body_small" className="font-medium capitalize text-admin_text_secondary text-sm">
              {propertyConfig.label || propertyKey}
            </Typography>
            <Input
              variant="admin"
              value={value}
              onChange={(e) => handlePropertyChange(propertyKey, e.target.value)}
              placeholder={propertyConfig.placeholder || ""}
              debounce={300}
            />
          </div>
        )
    }
  }

  return (
    <>
      <Panel
        variant="admin_bordered"
        defaultOpen={false}
        collapsible={true}
        scrollable={false}
        animated={true}
        title={titleElement}
        styleOverrides={{
          panel: "border-0 border-b border-admin_bordered rounded-none bg-admin_paper",
          header: "px-4 py-3 bg-admin_paper hover:bg-admin_neutral_1 transition-colors h-[50px] flex items-center justify-between",
          body: "p-0 bg-admin_canvas"
        }}
      >
        <div className="px-4 pb-4">
          <div className="ml-6 space-y-3">
            {Object.entries(group.properties).map(([propertyKey, propertyConfig]) =>
              renderPropertyEditor(propertyKey, propertyConfig)
            )}
          </div>
        </div>
      </Panel>

      {/* Color Picker Dropdown */}
      {activeColorPicker && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setActiveColorPicker(null)}
          />
          <div
            className="fixed z-50"
            style={{
              top: colorPickerPosition.top,
              left: colorPickerPosition.left,
              transform: colorPickerPosition.transform
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <ColorPicker
              initialColor={activeColorPicker.value}
              onChange={(color) => {
                handlePropertyChange(activeColorPicker.property, color)
              }}
              onColorSelect={(color) => {
                handlePropertyChange(activeColorPicker.property, color)
                setActiveColorPicker(null)
              }}
              onClose={() => setActiveColorPicker(null)}
              alpha={true}
              showFormatToggle={true}
              showSwatches={true}
            />
          </div>
        </>
      )}
    </>
  )
}

export default GenericPropertyGroupEditor